import {
  LYRRA_A11Y_STORAGE_KEY,
  LYRRA_A11Y_UPDATED_EVENT,
} from '@/hooks/useKeyboardNavigationSetting';

export interface A11ySettings {
  fontScale: number;
  lineHeight: number;
  letterSpacing: number;
  wordSpacing: number;
  highContrast: boolean;
  underlineLinks: boolean;
  dyslexiaFont: boolean;
  reducedMotion: boolean;
  keyboardNavigation: boolean;
  autoPlayImageDescriptionOnHover: boolean;
  mouseMagnifier: boolean;
  /** Bande opaque suivant le curseur : isole la ligne en cours (dyslexie/TDAH). */
  readingMask: boolean;
  /** Affiche un bouton "écouter" sur la sélection de texte (TTS à la demande). */
  speakOnSelection: boolean;
  simplifiedTextMode: boolean;
}

export type A11yPreset = 'standard' | 'malvoyance' | 'dys';

export const DEFAULT_A11Y_SETTINGS: A11ySettings = {
  fontScale: 1,
  lineHeight: 1.55,
  letterSpacing: 0,
  wordSpacing: 0,
  highContrast: false,
  underlineLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
  keyboardNavigation: false,
  autoPlayImageDescriptionOnHover: false,
  mouseMagnifier: false,
  readingMask: false,
  speakOnSelection: false,
  simplifiedTextMode: false,
};

export const A11Y_PRESET_SETTINGS: Record<A11yPreset, A11ySettings> = {
  standard: { ...DEFAULT_A11Y_SETTINGS },
  malvoyance: {
    ...DEFAULT_A11Y_SETTINGS,
    fontScale: 1.2,
    lineHeight: 1.7,
    highContrast: true,
    underlineLinks: true,
    autoPlayImageDescriptionOnHover: true,
    speakOnSelection: true,
  },
  dys: {
    ...DEFAULT_A11Y_SETTINGS,
    lineHeight: 1.9,
    letterSpacing: 0.05,
    wordSpacing: 0.12,
    dyslexiaFont: true,
    readingMask: true,
    speakOnSelection: true,
    simplifiedTextMode: true,
  },
};

/** Active/désactive le mode contraste renforcé sans changer le thème clair ou sombre. */
function applyHighContrastDocumentMode(enabled: boolean): void {
  const root = document.documentElement;
  root.classList.toggle('a11y-high-contrast', enabled);
  root.classList.toggle('high-contrast', enabled);
  void import('@/stores/ui.store').then(({ useUIStore, applyThemeToDocument }) => {
    applyThemeToDocument(useUIStore.getState().theme);
  });
}

export function readA11ySettingsFromStorage(): A11ySettings {
  try {
    const raw = localStorage.getItem(LYRRA_A11Y_STORAGE_KEY);
    return raw ? { ...DEFAULT_A11Y_SETTINGS, ...JSON.parse(raw) } : { ...DEFAULT_A11Y_SETTINGS };
  } catch {
    return { ...DEFAULT_A11Y_SETTINGS };
  }
}

export function applyA11ySettingsToDocument(s: A11ySettings): void {
  const root = document.documentElement;

  applyHighContrastDocumentMode(s.highContrast);
  root.classList.toggle('a11y-dys', s.dyslexiaFont);

  root.style.fontSize = s.fontScale !== 1 ? `${s.fontScale * 100}%` : '';

  let styleEl = document.getElementById('lyrra-a11y-inject') as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'lyrra-a11y-inject';
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = [
    `body, body p, body span, body li, body td, body h1, body h2, body h3, body h4, body h5, body label {
        line-height: ${s.lineHeight} !important;
        letter-spacing: ${s.letterSpacing}em !important;
        word-spacing: ${s.wordSpacing}em !important;
      }`,
    s.dyslexiaFont
      ? `
        body, body * { font-family: "OpenDyslexic", Arial, sans-serif !important; }
      `
      : '',
    s.underlineLinks
      ? `
        a, [role="link"] {
          text-decoration: underline !important;
          text-underline-offset: 3px !important;
        }
      `
      : '',
    s.reducedMotion
      ? `
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      `
      : '',
    s.highContrast
      ? `
        #lyrra-access-pad-indicator {
          background-color: #ffffff !important;
          color: #000000 !important;
          border: 3px solid #000000 !important;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35) !important;
          font-size: 1.0625rem !important;
          font-weight: 700 !important;
        }
        #lyrra-access-pad-indicator span,
        #lyrra-access-pad-indicator button,
        #lyrra-access-pad-indicator svg {
          color: #000000 !important;
          stroke: #000000 !important;
        }
        #lyrra-access-pad-indicator button {
          background-color: #f0f0f0 !important;
          border: 2px solid #000000 !important;
        }
        #lyrra-access-pad-indicator .lyrra-access-pad-indicator__status {
          background-color: #c45a00 !important;
          border: 2px solid #000000 !important;
        }
      `
      : '',
  ].join('\n');
}

export function persistA11ySettings(s: A11ySettings): void {
  localStorage.setItem(LYRRA_A11Y_STORAGE_KEY, JSON.stringify(s));
  applyA11ySettingsToDocument(s);
  queueMicrotask(() => window.dispatchEvent(new Event(LYRRA_A11Y_UPDATED_EVENT)));
}

/** Applique un préréglage accessibilité (ex. malvoyance pour le LyrraPad). */
export function applyLyrraA11yPreset(preset: A11yPreset): void {
  persistA11ySettings({ ...A11Y_PRESET_SETTINGS[preset] });
}

// ── Fusion intelligente de presets (cumul multi-handicap) ──────────────────

/** Clés numériques dont on prend le MAX (valeur la plus accommodante). */
const A11Y_NUMERIC_KEYS = ['fontScale', 'lineHeight', 'letterSpacing', 'wordSpacing'] as const;
type A11yNumericKey = (typeof A11Y_NUMERIC_KEYS)[number];

const A11Y_BOOLEAN_KEYS_FOR_MERGE = [
  'highContrast',
  'underlineLinks',
  'dyslexiaFont',
  'reducedMotion',
  'keyboardNavigation',
  'autoPlayImageDescriptionOnHover',
  'mouseMagnifier',
  'readingMask',
  'speakOnSelection',
  'simplifiedTextMode',
] as const;

/**
 * Fusionne un preset avec les réglages courants pour gérer les cas
 * multi-handicap (ex. malvoyance + dyslexie). Règles :
 * - booléens : OR logique (un preset n'éteint jamais une option déjà activée)
 * - valeurs numériques : MAX (interligne, taille, espacement)
 */
export function mergeA11yPresetIntoSettings(
  current: A11ySettings,
  preset: A11ySettings,
): A11ySettings {
  const next: A11ySettings = { ...current };
  for (const k of A11Y_BOOLEAN_KEYS_FOR_MERGE) {
    next[k] = current[k] || preset[k];
  }
  for (const k of A11Y_NUMERIC_KEYS) {
    next[k] = Math.max(current[k] ?? 0, preset[k] ?? 0);
  }
  return next;
}

/**
 * Retire un preset des réglages courants : remet aux valeurs par défaut UNIQUEMENT
 * les clés qui différaient du défaut dans ce preset, en respectant les autres profils
 * actifs (si une autre clé reste true ailleurs, on ne touche pas).
 */
export function removeA11yPresetFromSettings(
  current: A11ySettings,
  preset: A11ySettings,
  otherActivePresets: A11ySettings[],
): A11ySettings {
  const next: A11ySettings = { ...current };
  const otherUnion = otherActivePresets.reduce<A11ySettings>(
    (acc, p) => mergeA11yPresetIntoSettings(acc, p),
    { ...DEFAULT_A11Y_SETTINGS },
  );

  for (const k of A11Y_BOOLEAN_KEYS_FOR_MERGE) {
    if (preset[k] && !DEFAULT_A11Y_SETTINGS[k]) {
      next[k] = otherUnion[k];
    }
  }
  for (const k of A11Y_NUMERIC_KEYS) {
    const max = Math.max(DEFAULT_A11Y_SETTINGS[k], otherUnion[k]);
    if (preset[k] > DEFAULT_A11Y_SETTINGS[k]) {
      next[k] = max;
    }
  }
  return next;
}

/**
 * Indique si toutes les options du preset (true / valeurs > défaut) sont satisfaites
 * dans `current`. Permet d'afficher un badge « actif » même en mode multi-presets.
 */
export function isA11yPresetActiveOnSettings(
  current: A11ySettings,
  preset: A11ySettings,
): boolean {
  for (const k of A11Y_BOOLEAN_KEYS_FOR_MERGE) {
    if (preset[k] && !current[k]) return false;
  }
  for (const k of A11Y_NUMERIC_KEYS) {
    if (preset[k] > DEFAULT_A11Y_SETTINGS[k] && current[k] < preset[k]) return false;
  }
  // Au moins une option non-défaut doit être présente
  const hasAnyToggle = A11Y_BOOLEAN_KEYS_FOR_MERGE.some(
    (k) => preset[k] && !DEFAULT_A11Y_SETTINGS[k],
  );
  const hasAnyNumeric = A11Y_NUMERIC_KEYS.some(
    (k) => preset[k] > DEFAULT_A11Y_SETTINGS[k],
  );
  return hasAnyToggle || hasAnyNumeric;
}
